import { getRoomAt } from "../systems/rooms.js";
import { isLoaded } from "../systems/memory.js";
import { lockCutscene, unlockCutscene, isCutsceneLocked } from "../systems/cutsceneState.js";
import { onMemoryUIClose } from "../ui/memoryUI.js";
import { spawnStoryRamStickPickup } from "./ramPickup.js";
import { AUDIO_MUSIC, setStoryMusic } from "../systems/audio.js";

const HOMELESS_SCALE = 1.0;
const HOMELESS_FRAME_HALF_HEIGHT = 64;
const HOMELESS_VISUAL_Y_OFFSET = -HOMELESS_FRAME_HALF_HEIGHT * HOMELESS_SCALE;
const HOMELESS_Z = 9710;
const HOMELESS_SHADOW_Z = HOMELESS_Z - 2;
const HOMELESS_HEART_Z = HOMELESS_Z + 20;
const HOMELESS_SIGHT_RADIUS = 90;
const HOMELESS_WALK_DISTANCE = 96;
const HOMELESS_WALK_SPEED = 42;
const HOMELESS_RUN_SPEED = 110;
const HOMELESS_DOG_STOP_DISTANCE = 34;
const HOMELESS_SURPRISE_SECONDS = 0.45;
const CUTSCENE_ID = "homeless_ram_cutscene";
const ENDING_HOLD_SECONDS = 3;
const ENDING_FADE_SECONDS = 1;
const ENDING_FADE_Z = 20000;

const HOMELESS_ANIMS = {
    idle: { sprite: "homeless_idle", anim: "idle" },
    hurt: { sprite: "homeless_hurt", anim: "hurt" },
    walk: { sprite: "homeless_walk", anim: "walk" },
    run: { sprite: "homeless_run", anim: "run" },
};

function setHomelessAnimation(npc, state, facing = npc.facing || 1) {
    if (!npc.visual?.exists?.()) return;
    const next = HOMELESS_ANIMS[state] || HOMELESS_ANIMS.idle;
    const flipX = facing < 0;
    npc.facing = facing;
    if (npc.visualState === state && npc.visual.flipX === flipX) return;

    npc.visual.use(sprite(next.sprite, { anim: next.anim, flipX }));
    npc.visual.flipX = flipX;
    npc.visualState = state;

    if (npc.shadow?.exists?.()) {
        npc.shadow.use(sprite(next.sprite, {
            anim: next.anim,
            flipX,
            flipY: true,
        }));
        npc.shadow.flipX = flipX;
        npc.shadow.flipY = true;
    }
}

function moveToward(current, target, speed, dtVal) {
    const delta = target.sub(current);
    const dist = delta.len();
    if (dist <= 0.5) return target;
    return current.add(delta.unit().scale(Math.min(dist, speed * dtVal)));
}

function drawHeart(parent, localPos, scaleValue, delay) {
    const heart = parent.add([
        pos(localPos),
        opacity(1),
        z(HOMELESS_HEART_Z),
        {
            age: -delay,
            startY: localPos.y,
        },
    ]);

    heart.add([circle(4), pos(-3, -2), anchor("center"), color(255, 80, 135), opacity(0.9), scale(scaleValue)]);
    heart.add([circle(4), pos(3, -2), anchor("center"), color(255, 80, 135), opacity(0.9), scale(scaleValue)]);
    heart.add([rect(8, 8, { radius: 1 }), pos(0, 2), anchor("center"), rotate(45), color(255, 80, 135), opacity(0.9), scale(scaleValue)]);

    heart.onUpdate(() => {
        heart.age += dt();
        if (heart.age < 0) {
            heart.hidden = true;
            return;
        }

        heart.hidden = false;
        const t = heart.age % 1.4;
        // α.25+: assign a new Vec2 (in-place pos.y mutation skips transform updates)
        heart.pos = vec2(heart.pos.x, heart.startY - t * 18);
        heart.opacity = 1 - Math.max(0, t - 0.75) / 0.65;
    });
}

function spawnLoveHearts(parent, baseY) {
    drawHeart(parent, vec2(-10, baseY - 18), 0.85, 0);
    drawHeart(parent, vec2(4, baseY - 30), 1.0, 0.22);
    drawHeart(parent, vec2(16, baseY - 14), 0.75, 0.44);
}

function spawnSharedLoveHearts(npc, player) {
    if (npc.heartsShown) return;
    npc.heartsShown = true;
    spawnLoveHearts(npc, HOMELESS_VISUAL_Y_OFFSET);
    if (player?.exists?.()) {
        spawnLoveHearts(player, -34);
    }
}

function scheduleEnding(npc) {
    if (npc.endingScheduled) return;
    npc.endingScheduled = true;

    wait(ENDING_HOLD_SECONDS, () => {
        if (!npc.exists()) return;

        const fade = add([
            rect(width(), height()),
            pos(0, 0),
            color(0, 0, 0),
            opacity(0),
            fixed(),
            z(ENDING_FADE_Z),
            "ending_fade",
            { age: 0 },
        ]);

        fade.onUpdate(() => {
            fade.age += dt();
            fade.opacity = Math.min(1, fade.age / ENDING_FADE_SECONDS);
            if (fade.age >= ENDING_FADE_SECONDS) {
                go("credits");
            }
        });
    });
}

export function spawnHomelessGuy(worldPos, options = {}) {
    const startPos = vec2(worldPos.x, worldPos.y);
    const hasLineOfSight = options.hasLineOfSight || (() => true);
    const sightRadius = options.sightRadius ?? HOMELESS_SIGHT_RADIUS;

    const npc = add([
        pos(startPos),
        anchor("center"),
        z(HOMELESS_Z),
        "homeless_guy",
        {
            state: "idle",
            visualState: "idle",
            startPos,
            walkTarget: startPos.add(vec2(0, HOMELESS_WALK_DISTANCE)),
            surpriseTimer: 0,
            ramDropped: false,
            returned: false,
            heartsShown: false,
            endingScheduled: false,
            facing: 1,
            runTarget: null,
            closeHandle: null,
        },
    ]);

    npc.shadow = npc.add([
        sprite(HOMELESS_ANIMS.idle.sprite, {
            anim: HOMELESS_ANIMS.idle.anim,
            flipY: true,
        }),
        pos(0, 4),
        anchor("center"),
        scale(HOMELESS_SCALE * 0.9, HOMELESS_SCALE * 0.22),
        color(0, 0, 0),
        opacity(0.24),
        z(HOMELESS_SHADOW_Z),
    ]);

    npc.visual = npc.add([
        sprite(HOMELESS_ANIMS.idle.sprite, { anim: HOMELESS_ANIMS.idle.anim }),
        pos(0, HOMELESS_VISUAL_Y_OFFSET),
        anchor("center"),
        scale(HOMELESS_SCALE),
        color(255, 255, 255),
        z(HOMELESS_Z),
    ]);

    function canSeePlayer(player) {
        if (!player?.exists?.() || player.isDigging) return false;
        if (npc.pos.dist(player.pos) > sightRadius) return false;

        const npcRoom = getRoomAt(npc.pos);
        const playerRoom = getRoomAt(player.pos);
        if (!npcRoom || !playerRoom || npcRoom.id !== playerRoom.id) return false;

        return hasLineOfSight(npc.pos, player.pos);
    }

    function startCutscene() {
        if (npc.state !== "idle") return;
        npc.state = "surprised";
        npc.surpriseTimer = HOMELESS_SURPRISE_SECONDS;
        lockCutscene(CUTSCENE_ID);
        setHomelessAnimation(npc, "hurt");
    }

    function watchMemoryForOwner() {
        if (npc.closeHandle) return;
        npc.closeHandle = onMemoryUIClose(() => {
            if (!isLoaded("story_owner")) return;
            npc.closeHandle.cancel();
            npc.closeHandle = null;
            const player = get("player")[0];
            if (!player?.exists?.()) {
                spawnSharedLoveHearts(npc, null);
                return;
            }

            npc.state = "running_to_dog";
            npc.runTarget = vec2(player.pos.x, player.pos.y);
            lockCutscene(CUTSCENE_ID);
            setStoryMusic(AUDIO_MUSIC.spiritsForest);
            setHomelessAnimation(npc, "run");
        });
    }

    function dropRamStick() {
        if (npc.ramDropped) return;
        npc.ramDropped = true;
        spawnStoryRamStickPickup(npc.pos.add(vec2(0, 16)), {
            canCollect: () => npc.returned && !isCutsceneLocked(),
            onCollected: watchMemoryForOwner,
        });
    }

    npc.onUpdate(() => {
        if (!npc.exists()) return;

        const player = get("player")[0];
        if (npc.state === "idle") {
            setHomelessAnimation(npc, "idle");
            if (canSeePlayer(player)) startCutscene();
            return;
        }

        if (npc.state === "surprised") {
            npc.surpriseTimer -= dt();
            if (npc.surpriseTimer <= 0) {
                npc.state = "walking_down";
                setHomelessAnimation(npc, "walk", 1);
            }
            return;
        }

        if (npc.state === "walking_down") {
            setHomelessAnimation(npc, "walk", 1);
            npc.pos = moveToward(npc.pos, npc.walkTarget, HOMELESS_WALK_SPEED, dt());
            if (npc.pos.dist(npc.walkTarget) <= 1) {
                npc.pos = vec2(npc.walkTarget.x, npc.walkTarget.y);
                dropRamStick();
                npc.state = "walking_back";
            }
            return;
        }

        if (npc.state === "walking_back") {
            setHomelessAnimation(npc, "walk", -1);
            npc.pos = moveToward(npc.pos, npc.startPos, HOMELESS_WALK_SPEED, dt());
            if (npc.pos.dist(npc.startPos) <= 1) {
                npc.pos = vec2(npc.startPos.x, npc.startPos.y);
                npc.returned = true;
                npc.state = "done";
                setHomelessAnimation(npc, "idle", -1);
                unlockCutscene(CUTSCENE_ID);
            }
            return;
        }

        if (npc.state === "running_to_dog") {
            const targetPlayer = player?.exists?.() ? player : null;
            if (targetPlayer) {
                npc.runTarget = vec2(targetPlayer.pos.x, targetPlayer.pos.y);
            }

            const target = npc.runTarget || npc.pos;
            setHomelessAnimation(npc, "run", target.x < npc.pos.x ? -1 : 1);
            npc.pos = moveToward(npc.pos, target, HOMELESS_RUN_SPEED, dt());

            if (!targetPlayer || npc.pos.dist(target) <= HOMELESS_DOG_STOP_DISTANCE) {
                npc.state = "reunited";
                setHomelessAnimation(npc, "idle");
                spawnSharedLoveHearts(npc, targetPlayer);
                unlockCutscene(CUTSCENE_ID);
                scheduleEnding(npc);
            }
            return;
        }

        setHomelessAnimation(npc, "idle");
    });

    npc.onDestroy(() => {
        unlockCutscene(CUTSCENE_ID);
        if (npc.closeHandle) npc.closeHandle.cancel();
    });

    return npc;
}
